import type { ClientKind } from '@/kinds/kind'
import {
  conceptAfterPatch,
  conceptApply,
  conceptDefaults,
  conceptInitialStatus,
  conceptName,
  courseInitialStatus,
  exerciseApply,
  exerciseDefaults,
  exerciseInitialStatus,
  exerciseName,
  lessonApply,
  lessonInitialStatus,
  moduleInitialStatus,
  noActions,
  sessionApply,
  sessionDefaults,
  sessionInitialStatus,
  sessionName,
  titled,
  withTitle,
} from './apply'
import {
  conceptSpec,
  conceptStatus,
  courseSpec,
  courseStatus,
  exerciseSpec,
  exerciseStatus,
  lessonSpec,
  lessonStatus,
  moduleSpec,
  sessionSpec,
  sessionStatus,
  moduleStatus,
  type ConceptSpec,
  type ConceptStatus,
  type CourseSpec,
  type CourseStatus,
  type ExerciseSpec,
  type ExerciseStatus,
  type LessonSpec,
  type LessonStatus,
  type ModuleSpec,
  type ModuleStatus,
  type SessionSpec,
  type SessionStatus,
} from './schema'

// The views live in src/views/learn/; the kinds themselves draw nothing.
const Nothing = () => null
const shared = {
  version: 1,
  validate: () => [],
  workspaceScoped: true,
  ListRow: Nothing,
  Detail: Nothing,
  Form: Nothing,
}

export const courseKind: ClientKind<CourseSpec, CourseStatus> = {
  ...shared,
  name: 'Course',
  spec: courseSpec,
  status: courseStatus,
  defaults: withTitle<CourseSpec>(),
  initialStatus: courseInitialStatus,
  displayName: titled,
  apply: noActions,
  actions: [],
}

export const moduleKind: ClientKind<ModuleSpec, ModuleStatus> = {
  ...shared,
  name: 'Module',
  spec: moduleSpec,
  status: moduleStatus,
  defaults: withTitle<ModuleSpec>(),
  initialStatus: moduleInitialStatus,
  displayName: titled,
  apply: noActions,
  actions: [],
}

export const lessonKind: ClientKind<LessonSpec, LessonStatus> = {
  ...shared,
  name: 'Lesson',
  spec: lessonSpec,
  status: lessonStatus,
  defaults: withTitle<LessonSpec>(),
  initialStatus: lessonInitialStatus,
  displayName: titled,
  apply: lessonApply,
  actions: ['mark_done', 'skip', 'reopen'],
}

export const conceptKind: ClientKind<ConceptSpec, ConceptStatus> = {
  ...shared,
  name: 'Concept',
  spec: conceptSpec,
  status: conceptStatus,
  defaults: conceptDefaults,
  initialStatus: conceptInitialStatus,
  displayName: conceptName,
  apply: conceptApply,
  afterPatch: conceptAfterPatch,
  actions: ['set_familiarity'],
}

export const sessionKind: ClientKind<SessionSpec, SessionStatus> = {
  ...shared,
  name: 'Session',
  spec: sessionSpec,
  status: sessionStatus,
  defaults: sessionDefaults,
  initialStatus: sessionInitialStatus,
  displayName: sessionName,
  apply: sessionApply,
  actions: ['item_done', 'item_undone', 'end', 'resume'],
}

export const exerciseKind: ClientKind<ExerciseSpec, ExerciseStatus> = {
  ...shared,
  name: 'Exercise',
  spec: exerciseSpec,
  status: exerciseStatus,
  defaults: exerciseDefaults,
  initialStatus: exerciseInitialStatus,
  displayName: exerciseName,
  apply: exerciseApply,
  actions: ['answer', 'reset'],
}

export * from './schema'
export type {
  ConceptResource,
  CourseResource,
  ModuleResource,
  LessonResource,
  SessionResource,
} from './apply'
